import { useState } from 'react';
import { Pencil, Plus, Save, Trash2, X, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateFR, formatEUR } from '../utils';
import type { Facture } from '../types';

export interface EchRow {
  idx: number;
  label: string;
  date?: string;
  montant: number;
  paid: boolean;
  paid_at?: string;
}

export function calcTotaux(f: Facture) {
  const totalHT = f.lignes.reduce((s, l) => s + (l.quantite || 0) * (l.prixUnitaireHT || 0), 0);
  const remise = totalHT * ((f.remisePct || 0) / 100);
  const htNet = totalHT - remise;
  const tva = f.lignes.reduce((s, l) => {
    const lht = (l.quantite || 0) * (l.prixUnitaireHT || 0);
    const lhtNet = lht * (1 - (f.remisePct || 0) / 100);
    return s + lhtNet * ((l.tvaPct || 0) / 100);
  }, 0);
  const ttc = htNet + tva;
  return { totalHT, remise, htNet, tva, ttc };
}

export function getEcheances(f: Facture): EchRow[] {
  const ech = f.echeancier || [];
  if (ech.length === 0) {
    const { ttc } = calcTotaux(f);
    return [{ idx: 0, label: 'Solde', date: f.dateEcheance, montant: ttc, paid: f.statut === 'Payée', paid_at: f.datePaiement }];
  }
  return ech.map((e, idx) => ({
    idx,
    label: e.label || `Échéance ${idx + 1}`,
    date: e.date,
    montant: Number(e.montant) || 0,
    paid: !!(e as any).paid,
    paid_at: (e as any).paid_at,
  }));
}

function round2(n: number) { return Math.round(n * 100) / 100; }

// ────────────────────────────────────────────────────────────────────────────
// FactureBlock — affichage facture + éditeur d'échéancier inline
// ────────────────────────────────────────────────────────────────────────────

export function FactureBlock({ facture: f, ech, isEditing, onEdit, onCloseEdit, onTogglePaid, onSave }: {
  facture: Facture;
  ech: EchRow[];
  isEditing: boolean;
  onEdit: () => void;
  onCloseEdit: () => void;
  onTogglePaid: (idx: number) => void;
  onSave: (newEch: EchRow[]) => void | Promise<void>;
}) {
  const { ttc } = calcTotaux(f);
  return (
    <div className="border-b border-slate-800/60 last:border-b-0">
      <div className="px-4 py-2 flex items-center justify-between bg-slate-900/40 gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-xs text-amber-300">{f.numero}</span>
          <span className="text-xs text-slate-400">Émise le {formatDateFR(f.dateEmission)}</span>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">{f.statut}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-400">Total : {formatEUR(ttc)}</div>
          {!isEditing ? (
            <button
              onClick={onEdit}
              className="px-2.5 py-1 text-[11px] rounded border border-slate-700 text-slate-300 hover:bg-slate-800/60 inline-flex items-center gap-1.5"
              title="Configurer les traites"
            >
              <Pencil size={11} strokeWidth={1.5} /> Traites
            </button>
          ) : (
            <button
              onClick={onCloseEdit}
              className="px-2.5 py-1 text-[11px] rounded border border-slate-700 text-slate-400 hover:bg-slate-800/60 inline-flex items-center gap-1.5"
            >
              <X size={11} strokeWidth={1.5} /> Fermer
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <EcheancierEditor facture={f} ech={ech} onSave={onSave} onCancel={onCloseEdit} />
      ) : (
        <div className="divide-y divide-slate-800/60">
          {ech.map((e, i) => {
            const today = new Date().toISOString().slice(0, 10);
            const late = !e.paid && e.date && e.date < today;
            return (
              <button
                key={i}
                onClick={() => onTogglePaid(i)}
                className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-slate-900/40 text-left"
              >
                {e.paid ? (
                  <CheckCircle2 size={18} strokeWidth={1.5} className="text-emerald-400 shrink-0" />
                ) : (
                  <Circle size={18} strokeWidth={1.5} className={`shrink-0 ${late ? 'text-rose-400' : 'text-slate-500'}`} />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${e.paid ? 'text-emerald-200 line-through decoration-emerald-500/40' : 'text-slate-200'}`}>{e.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {e.date ? `Échéance : ${formatDateFR(e.date)}` : 'Sans date'}
                    {e.paid && e.paid_at && ` · payée le ${formatDateFR(e.paid_at)}`}
                    {late && ' · en retard'}
                  </div>
                </div>
                <div className={`text-sm tabular-nums ${e.paid ? 'text-emerald-300' : late ? 'text-rose-300' : 'text-slate-200'}`}>{formatEUR(e.montant)}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// EcheancierEditor — tableau d'édition des traites
// ────────────────────────────────────────────────────────────────────────────

export function EcheancierEditor({ facture: f, ech, onSave, onCancel }: {
  facture: Facture;
  ech: EchRow[];
  onSave: (e: EchRow[]) => void | Promise<void>;
  onCancel: () => void;
}) {
  const { ttc } = calcTotaux(f);
  const [rows, setRows] = useState<EchRow[]>(() => ech.map((e, i) => ({ ...e, idx: i })));

  const total = rows.reduce((s, r) => s + (Number(r.montant) || 0), 0);
  const diff = round2(ttc - total);

  const upd = (i: number, patch: Partial<EchRow>) => {
    setRows((prev) => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  };
  const add = () => {
    const remaining = Math.max(0, round2(ttc - total));
    setRows((prev) => [...prev, { idx: prev.length, label: `Traite ${prev.length}`, date: '', montant: remaining, paid: false }]);
  };
  const remove = (i: number) => setRows((prev) => prev.filter((_, k) => k !== i));

  const applyPreset = (preset: 'solde' | 'acompte30-solde' | 'acompte30-2traites-solde' | 'acompte30-3traites-solde' | 'monthly3' | 'monthly6') => {
    const eventDate = f.dateEcheance || new Date().toISOString().slice(0, 10);
    const dPlus = (iso: string, months: number) => {
      const d = new Date(iso);
      d.setMonth(d.getMonth() + months);
      return d.toISOString().slice(0, 10);
    };
    if (preset === 'solde') {
      setRows([{ idx: 0, label: 'Solde', date: eventDate, montant: round2(ttc), paid: false }]);
      return;
    }
    if (preset === 'acompte30-solde') {
      const a = round2(ttc * 0.3);
      setRows([
        { idx: 0, label: 'Acompte 30%', date: new Date().toISOString().slice(0, 10), montant: a, paid: false },
        { idx: 1, label: 'Solde', date: eventDate, montant: round2(ttc - a), paid: false },
      ]);
      return;
    }
    if (preset === 'acompte30-2traites-solde' || preset === 'acompte30-3traites-solde') {
      const nTraites = preset === 'acompte30-2traites-solde' ? 2 : 3;
      const a = round2(ttc * 0.3);
      const reste = round2(ttc - a);
      const tMontant = round2(reste / (nTraites + 1));
      const today = new Date().toISOString().slice(0, 10);
      const arr: EchRow[] = [{ idx: 0, label: 'Acompte 30%', date: today, montant: a, paid: false }];
      for (let i = 1; i <= nTraites; i++) arr.push({ idx: arr.length, label: `Traite ${i}`, date: dPlus(today, i), montant: tMontant, paid: false });
      const dejaPlanifie = round2(a + tMontant * nTraites);
      arr.push({ idx: arr.length, label: 'Solde', date: eventDate, montant: round2(ttc - dejaPlanifie), paid: false });
      setRows(arr);
      return;
    }
    if (preset === 'monthly3' || preset === 'monthly6') {
      const n = preset === 'monthly3' ? 3 : 6;
      const part = round2(ttc / n);
      const today = new Date().toISOString().slice(0, 10);
      const arr: EchRow[] = [];
      for (let i = 0; i < n; i++) {
        const montant = i === n - 1 ? round2(ttc - part * (n - 1)) : part;
        arr.push({ idx: i, label: i === 0 ? 'Mensualité 1' : (i === n - 1 ? `Mensualité ${n} (solde)` : `Mensualité ${i + 1}`), date: dPlus(today, i), montant, paid: false });
      }
      setRows(arr);
      return;
    }
  };

  const equilibrer = () => {
    if (rows.length === 0) return;
    const last = rows.length - 1;
    const sumOther = rows.slice(0, last).reduce((s, r) => s + (Number(r.montant) || 0), 0);
    upd(last, { montant: round2(ttc - sumOther) });
  };

  return (
    <div className="p-4 bg-slate-950/40 space-y-3">
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="text-slate-500 uppercase tracking-widest mr-1 py-1">Modèles :</span>
        <PresetBtn onClick={() => applyPreset('solde')}>Solde unique</PresetBtn>
        <PresetBtn onClick={() => applyPreset('acompte30-solde')}>Acompte&nbsp;30% + Solde</PresetBtn>
        <PresetBtn onClick={() => applyPreset('acompte30-2traites-solde')}>Acompte + 2&nbsp;traites + Solde</PresetBtn>
        <PresetBtn onClick={() => applyPreset('acompte30-3traites-solde')}>Acompte + 3&nbsp;traites + Solde</PresetBtn>
        <PresetBtn onClick={() => applyPreset('monthly3')}>3 mensualités</PresetBtn>
        <PresetBtn onClick={() => applyPreset('monthly6')}>6 mensualités</PresetBtn>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left py-1.5 pr-2 w-10">#</th>
              <th className="text-left py-1.5 pr-2">Libellé</th>
              <th className="text-left py-1.5 pr-2 w-40">Échéance</th>
              <th className="text-right py-1.5 pr-2 w-32">Montant TTC</th>
              <th className="text-center py-1.5 px-2 w-20">Payée</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-800/60">
                <td className="py-1.5 pr-2 text-slate-500">{i + 1}</td>
                <td className="py-1.5 pr-2">
                  <input
                    value={r.label}
                    onChange={(e) => upd(i, { label: e.target.value })}
                    className="w-full bg-slate-900/60 border border-slate-800 rounded px-2 py-1 text-slate-200"
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <input
                    type="date"
                    value={r.date || ''}
                    onChange={(e) => upd(i, { date: e.target.value })}
                    className="w-full bg-slate-900/60 border border-slate-800 rounded px-2 py-1 text-slate-200"
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <input
                    type="number"
                    step="0.01"
                    value={r.montant}
                    onChange={(e) => upd(i, { montant: Number(e.target.value) || 0 })}
                    className="w-full bg-slate-900/60 border border-slate-800 rounded px-2 py-1 text-right tabular-nums text-slate-200"
                  />
                </td>
                <td className="py-1.5 px-2 text-center">
                  <input
                    type="checkbox"
                    checked={r.paid}
                    onChange={(e) => upd(i, { paid: e.target.checked, paid_at: e.target.checked ? (r.paid_at || new Date().toISOString().slice(0, 10)) : undefined })}
                    className="accent-emerald-500"
                  />
                </td>
                <td className="py-1.5 text-right">
                  <button onClick={() => remove(i)} className="text-slate-500 hover:text-rose-400" title="Supprimer">
                    <Trash2 size={13} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
        <div className="flex items-center gap-2">
          <button onClick={add} className="px-2.5 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800/60 inline-flex items-center gap-1.5">
            <Plus size={12} strokeWidth={1.5} /> Ajouter une traite
          </button>
          <button onClick={equilibrer} className="px-2.5 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800/60">
            Équilibrer sur la dernière
          </button>
        </div>
        <div className="flex items-center gap-3 tabular-nums">
          <span className="text-slate-500">Total facture :</span>
          <span className="text-slate-200">{formatEUR(ttc)}</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-500">Somme traites :</span>
          <span className={`${Math.abs(diff) < 0.01 ? 'text-emerald-300' : 'text-rose-300'}`}>{formatEUR(total)}</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-500">Écart :</span>
          <span className={`${Math.abs(diff) < 0.01 ? 'text-emerald-300' : 'text-rose-300'}`}>{formatEUR(diff)}</span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs rounded border border-slate-700 text-slate-400 hover:bg-slate-800/60">Annuler</button>
        <button
          onClick={() => {
            if (Math.abs(diff) >= 0.01) {
              toast.warning(`La somme des traites (${formatEUR(total)}) ne correspond pas au total TTC (${formatEUR(ttc)}). Écart : ${formatEUR(diff)}.`);
            }
            onSave(rows);
          }}
          className="px-3 py-1.5 text-xs rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 inline-flex items-center gap-1.5"
        >
          <Save size={12} strokeWidth={1.5} /> Enregistrer l'échéancier
        </button>
      </div>
    </div>
  );
}

function PresetBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800/60 hover:text-amber-200 transition"
    >
      {children}
    </button>
  );
}
